import { io, Socket } from 'socket.io-client';
import api from './api';
import { BASE_URL } from '../config';

export interface ChatMessage {
  id: number;
  sender_id: number;
  sender_role: 'customer' | 'admin' | 'super_admin' | 'agent' | 'technician' | 'manager';
  recipient_id?: number; // ID du destinataire (pour les messages admin → client)
  message: string;
  is_read: boolean;
  created_at: string;
  updated_at: string;
  sender?: {
    id: number;
    name?: string;
    username?: string;
    email?: string;
  };
}

export interface TypingUser {
  userId: number;
  userName: string;
  userRole: string;
}

class ChatService {
  private socket: Socket | null = null;
  private messageCallbacks: Array<(message: ChatMessage) => void> = [];
  private messageSentCallbacks: Array<(data: { success: boolean; message: ChatMessage }) => void> = [];
  private typingCallbacks: Array<(data: TypingUser) => void> = [];
  private stopTypingCallbacks: Array<(data: TypingUser) => void> = [];
  private connectionCallbacks: Array<(connected: boolean) => void> = [];
  private messagesReadCallbacks: Array<(data: { messageIds: number[] }) => void> = [];
  private isConnected = false;
  
  // 💾 Cache persistant des messages
  private cachedMessages: ChatMessage[] = [];
  private readonly CACHE_KEY = 'chat_messages_cache';
  private cacheRecipientId: number | null = null; // Pour identifier le cache de quelle conversation

  connect(token: string, _userId: string): void {
    // Si le socket existe déjà et est connecté, ne rien faire
    if (this.socket && this.socket.connected) {
      console.log('💬 Socket déjà connecté, réutilisation de la connexion existante');
      return;
    }

    // Si le socket existe mais n'est pas connecté, le supprimer d'abord
    if (this.socket && !this.socket.connected) {
      console.log('💬 Nettoyage de l\'ancien socket déconnecté');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    // ⚠️ NE PAS effacer les callbacks existants !
    // Les composants qui ont enregistré des listeners doivent les garder
    // this.messageCallbacks = [];
    // this.typingCallbacks = [];
    // this.stopTypingCallbacks = [];
    // this.connectionCallbacks = [];

    // Utiliser BASE_URL dynamique (détection Sandbox vs Production vs Local)
    const socketUrl = BASE_URL;
    const effectiveToken = token || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    
    console.log('🔌 Création d\'une nouvelle connexion Socket.IO au serveur:', socketUrl);

    this.socket = io(socketUrl, {
      path: '/socket.io/',
      auth: { token: effectiveToken },
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      withCredentials: true,
    });

    this.socket.on('connect', () => {
      console.log('💬 Chat admin connecté au serveur');
      this.isConnected = true;
      this.connectionCallbacks.forEach(cb => cb(true));

      // S'authentifier explicitement avec le token JWT
      this.socket?.emit('chat:authenticate', { token: effectiveToken });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('💬 Chat admin déconnecté:', reason);
      this.isConnected = false;
      this.connectionCallbacks.forEach(cb => cb(false));
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ [ChatService] Erreur connexion Socket.IO:', error);
      this.isConnected = false;
      this.connectionCallbacks.forEach(cb => cb(false));
    });

    this.socket.io.on('reconnect', () => {
      console.log('🔄 [ChatService] Socket.IO reconnecté');
      this.isConnected = true;
      this.connectionCallbacks.forEach(cb => cb(true));
      this.socket?.emit('chat:authenticate', { token: effectiveToken });
    });

    this.socket.on('chat:authenticated', (data) => {
      console.log('💬 Admin authentifié:', data.message);
    });

    this.socket.on('chat:new_message', (data: ChatMessage) => {
      console.log('💬 Nouveau message reçu:', data);
      this.messageCallbacks.forEach(cb => cb(data));
    });

    this.socket.on('chat:message_sent', (data: { success: boolean; message: ChatMessage }) => {
      console.log('💬 Message envoyé confirmé:', data);
      // Notifier pour mettre à jour l'ID du message optimiste
      this.messageSentCallbacks.forEach(cb => cb(data));
    });

    this.socket.on('chat:user_typing', (data: TypingUser) => {
      console.log('💬 Utilisateur en train d\'écrire:', data);
      this.typingCallbacks.forEach(cb => cb(data));
    });

    this.socket.on('chat:user_stop_typing', (data: TypingUser) => {
      console.log('💬 Utilisateur a arrêté d\'écrire:', data);
      this.stopTypingCallbacks.forEach(cb => cb(data));
    });

    this.socket.on('chat:message_read', (data) => {
      console.log('💬 Messages marqués comme lus:', data);
    });

    // Écouter l'événement global de messages lus
    this.socket.on('chat:messages_read', (data: { messageIds: number[] }) => {
      console.log('💬 Messages lus (broadcast):', data);
      this.messagesReadCallbacks.forEach(cb => cb(data));
    });

    this.socket.on('error', (error) => {
      console.error('❌ Erreur Socket.IO:', error);
    });
  }

  sendMessage(message: string, recipientId?: number): void {
    if (!this.socket || !this.socket.connected) {
      console.error('❌ Socket non connecté');
      return;
    }

    this.socket.emit('chat:send_message', {
      message,
      sender_role: 'admin',
      recipient_id: recipientId, // ID du client destinataire
    });
  }

  startTyping(): void {
    if (!this.socket || !this.socket.connected) return;
    this.socket.emit('chat:typing');
  }

  stopTyping(): void {
    if (!this.socket || !this.socket.connected) return;
    this.socket.emit('chat:stop_typing');
  }

  markAsRead(messageIds: number[]): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ [ChatService] Impossible de marquer comme lu: socket non connecté');
      return;
    }
    console.log('📖 [ChatService] Marquage comme lus:', messageIds.length, 'message(s)', messageIds);
    this.socket.emit('chat:mark_read', { messageIds });
  }

  onMessage(callback: (message: ChatMessage) => void): void {
    this.messageCallbacks.push(callback);
  }

  offMessage(callback: (message: ChatMessage) => void): void {
    const index = this.messageCallbacks.indexOf(callback);
    if (index > -1) {
      this.messageCallbacks.splice(index, 1);
    }
  }

  clearMessageCallbacks(): void {
    this.messageCallbacks = [];
  }

  onTyping(callback: (data: TypingUser) => void): void {
    this.typingCallbacks.push(callback);
  }

  onStopTyping(callback: (data: TypingUser) => void): void {
    this.stopTypingCallbacks.push(callback);
  }

  offTyping(callback: (data: TypingUser) => void): void {
    const index = this.typingCallbacks.indexOf(callback);
    if (index > -1) {
      this.typingCallbacks.splice(index, 1);
    }
  }

  offStopTyping(callback: (data: TypingUser) => void): void {
    const index = this.stopTypingCallbacks.indexOf(callback);
    if (index > -1) {
      this.stopTypingCallbacks.splice(index, 1);
    }
  }

  onConnectionChange(callback: (connected: boolean) => void): void {
    this.connectionCallbacks.push(callback);
    // Appeler immédiatement le callback avec l'état actuel de la connexion
    callback(this.isConnected);
  }

  onMessagesRead(callback: (data: { messageIds: number[] }) => void): void {
    this.messagesReadCallbacks.push(callback);
  }

  offMessagesRead(callback: (data: { messageIds: number[] }) => void): void {
    const index = this.messagesReadCallbacks.indexOf(callback);
    if (index > -1) {
      this.messagesReadCallbacks.splice(index, 1);
    }
  }

  onMessageSent(callback: (data: { success: boolean; message: ChatMessage }) => void): void {
    this.messageSentCallbacks.push(callback);
  }

  offMessageSent(callback: (data: { success: boolean; message: ChatMessage }) => void): void {
    const index = this.messageSentCallbacks.indexOf(callback);
    if (index > -1) {
      this.messageSentCallbacks.splice(index, 1);
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  // 💾 Gestion du cache persistant
  
  /**
   * Charge le cache depuis localStorage pour un recipientId spécifique
   */
  loadCachedMessages(recipientId?: number): ChatMessage[] {
    try {
      const cacheKey = recipientId ? `${this.CACHE_KEY}_${recipientId}` : this.CACHE_KEY;
      const cacheString = localStorage.getItem(cacheKey);
      
      if (cacheString) {
        const cached = JSON.parse(cacheString) as ChatMessage[];
        this.cachedMessages = cached;
        this.cacheRecipientId = recipientId || null;
        console.log(`💾 [ChatService] ${cached.length} messages chargés depuis le cache (recipientId: ${recipientId || 'all'})`);
        return cached;
      }
    } catch (error) {
      console.error('⚠️ Erreur chargement cache:', error);
    }
    return [];
  }

  /**
   * Sauvegarde le cache dans localStorage
   */
  private saveCacheToStorage(recipientId?: number): void {
    try {
      const cacheKey = recipientId ? `${this.CACHE_KEY}_${recipientId}` : this.CACHE_KEY;
      localStorage.setItem(cacheKey, JSON.stringify(this.cachedMessages));
      console.log(`💾 [ChatService] ${this.cachedMessages.length} messages sauvegardés dans le cache (recipientId: ${recipientId || 'all'})`);
    } catch (error) {
      console.error('⚠️ Erreur sauvegarde cache:', error);
    }
  }

  /**
   * Ajoute un message au cache
   */
  addMessageToCache(message: ChatMessage, recipientId?: number): void {
    // Vérifier si le message n'existe pas déjà
    const exists = this.cachedMessages.some(msg => 
      msg.id === message.id || 
      (msg.message === message.message && 
       msg.sender_id === message.sender_id && 
       Math.abs(new Date(msg.created_at).getTime() - new Date(message.created_at).getTime()) < 2000)
    );
    
    if (!exists) {
      this.cachedMessages.push(message);
      this.saveCacheToStorage(recipientId);
    }
  }

  /**
   * Met à jour tous les messages du cache
   */
  setCachedMessages(messages: ChatMessage[], recipientId?: number): void {
    this.cachedMessages = [...messages];
    this.cacheRecipientId = recipientId || null;
    this.saveCacheToStorage(recipientId);
  }

  /**
   * Récupère le cache actuel
   */
  getCachedMessages(): ChatMessage[] {
    return [...this.cachedMessages];
  }

  /**
   * Attribution d'une conversation à l'agent connecté
   */
  async assignConversation(targetUserId: number): Promise<any> {
    try {
      const response = await api.post(`/chat/conversations/${targetUserId}/assign`);
      return response.data;
    } catch (error) {
      console.error(`Erreur attribution conversation ${targetUserId}:`, error);
      throw error;
    }
  }

  /**
   * Clôture d'une conversation support avec compte-rendu obligatoire
   */
  async closeConversation(targetUserId: number, category: string, notes: string): Promise<any> {
    try {
      const response = await api.post(`/chat/conversations/${targetUserId}/close`, { category, notes });
      return response.data;
    } catch (error) {
      console.error(`Erreur clôture conversation ${targetUserId}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer les données Fiche Client 360°
   */
  async getCustomer360Data(targetUserId: number): Promise<any> {
    try {
      const response = await api.get(`/chat/customer-360/${targetUserId}`);
      return response.data.data;
    } catch (error) {
      console.error(`Erreur chargement Fiche Client 360° pour user ${targetUserId}:`, error);
      throw error;
    }
  }

  /**
   * Vide le cache
   */
  clearCache(recipientId?: number): void {
    try {
      const cacheKey = recipientId ? `${this.CACHE_KEY}_${recipientId}` : this.CACHE_KEY;
      localStorage.removeItem(cacheKey);
      if (!recipientId || recipientId === this.cacheRecipientId) {
        this.cachedMessages = [];
        this.cacheRecipientId = null;
      }
      console.log(`💾 [ChatService] Cache vidé (recipientId: ${recipientId || 'all'})`);
    } catch (error) {
      console.error('⚠️ Erreur nettoyage cache:', error);
    }
  }
}

export const chatService = new ChatService();
